import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { environment } from 'src/environments/environment';

/**
 * Map Analysis Checkout
 *
 * Calls the Hub's hosted Stripe Checkout endpoint with `acres + fieldId`.
 * The Hub computes `quantity = max(1, ceil(acres))` and creates a Checkout
 * Session against the per-acre Stripe Price — the dollar amount is LOCKED
 * at session-creation time on the server. The client never has to trust
 * its own math.
 *
 * Fresh-session rule (billing-payments § 5.1.3 / § 9): every CTA tap mints
 * a NEW session. We never reuse a stale URL, even on the same field —
 * Stripe pricing or acreage may have changed between attempts.
 *
 * Per billing-payments § 1.0 (Q0): the web NEVER imports `@stripe/stripe-js`.
 * All Stripe interaction is via hosted-page redirect.
 */
export interface CheckoutSessionResult {
  /** Stripe-hosted Checkout URL. Assign to window.location to redirect. */
  url: string;
  /** Stripe Checkout Session ID (cs_xxx). */
  sessionId: string;
  /** Quantity the session was created with: max(1, ceil(acres)). */
  billedAcres: number;
}

/**
 * Shape returned by POST /payments/map-analysis/purchase/from-checkout-session.
 * Mirrors the adn_map_analysis_purchases row the Hub upserts.
 */
export interface FinalizedPurchase {
  id: string;
  organizationId: string;
  fieldId: string;
  stripeCheckoutSessionId: string;
  stripePaymentIntentId: string | null;
  requestedAcres: number;
  billedAcres: number;
  unitPriceCents: number;
  amountPaid: number;
  currency: string;
  status: 'pending' | 'completed' | 'refunded' | 'failed';
}

export interface CreateCheckoutParams {
  acres: number;
  fieldId: string;
  productKey?: string;
  /** Optional override; sanitized + allowlisted server-side. */
  returnPath?: string;
}

@Injectable({ providedIn: 'root' })
export class MapAnalysisCheckoutService {
  private http = inject(HttpClient);
  private apiUrl = environment.apiUrl;

  async createCheckoutSession(params: CreateCheckoutParams): Promise<CheckoutSessionResult> {
    const response = await firstValueFrom(
      this.http.post<{ success: boolean; data: CheckoutSessionResult; error?: string }>(
        `${this.apiUrl}/payments/map-analysis/checkout-session`,
        {
          acres: params.acres,
          fieldId: params.fieldId,
          productKey: params.productKey || 'map_analysis_per_acre',
          ...(params.returnPath ? { returnPath: params.returnPath } : {}),
        },
      ),
    );
    if (!response?.success || !response.data?.url) {
      throw new Error(response?.error || 'Failed to create checkout session');
    }
    return response.data;
  }

  /**
   * Redirect the browser to Stripe Checkout. The user leaves the SPA and
   * returns via the success_url (set server-side from STRIPE_RETURN_BASE_URL_WEB).
   */
  redirectToCheckout(session: CheckoutSessionResult): void {
    window.location.assign(session.url);
  }

  /**
   * Called by the return route after Stripe redirects back. Foreground-
   * syncs the purchase record from Stripe so the subsequent createAnalysis
   * call has a confirmed audit row to link against — even if the webhook
   * is delayed. Idempotent server-side: if the webhook already landed,
   * this is a no-op. See billing-payments § 9.4.
   *
   * Returns the persisted purchase row.
   */
  async finalizePurchase(sessionId: string): Promise<FinalizedPurchase> {
    const response = await firstValueFrom(
      this.http.post<{ success: boolean; data: FinalizedPurchase; error?: string }>(
        `${this.apiUrl}/payments/map-analysis/purchase/from-checkout-session`,
        { sessionId },
      ),
    );
    if (!response?.success || !response.data) {
      throw new Error(response?.error || 'Failed to finalize purchase');
    }
    return response.data;
  }
}
